import axios from 'axios';
import { baseAPI } from '../config';

/**
 * Canal de correo de AR (Resend): alta y verificación del dominio de una company.
 *
 * Va contra `/email-resend`, NO contra `/emails`, que es la bandeja de Gmail de
 * recobro y es admin-only. Se diferencian en algo más que una letra a propósito.
 */
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

/** Qué ofrece este entorno: si la vía gestionada está disponible y con qué dominio padre. */
export const getEmailConfig = async () => {
  const { data } = await axios.get(`${baseAPI}/email-resend/config`, {
    headers: authHeaders(),
  });
  return data.data;
};

/** Dominios de correo de una company. */
export const listCompanyEmailDomains = async companyId => {
  const { data } = await axios.get(`${baseAPI}/email-resend/companies/${companyId}/domains`, {
    headers: authHeaders(),
  });
  return data.data;
};

/**
 * Da de alta el dominio.
 * @param {Object} payload { mode, label|domain, from_local_part, display_name }
 */
export const createCompanyEmailDomain = async (companyId, payload) => {
  const { data } = await axios.post(`${baseAPI}/email-resend/companies/${companyId}/domains`, payload, {
    headers: authHeaders(),
  });
  return data.data;
};

/**
 * Relee el estado en Resend. Con `force` le pide que compruebe los registros
 * ahora, en vez de esperar a su propio ciclo — es el botón «Comprobar ahora».
 */
export const syncEmailDomain = async (domainId, { force = false } = {}) => {
  const { data } = await axios.post(
    `${baseAPI}/email-resend/domains/${domainId}/sync${force ? '?force=true' : ''}`,
    {},
    { headers: authHeaders() }
  );
  return data.data;
};

/**
 * Todos los dominios con capacidad y salud. Admin-only: cruza tenants, así que
 * el backend lo protege por rol y no por `canManageCompanyEmail`.
 */
export const listAllEmailDomains = async () => {
  const { data } = await axios.get(`${baseAPI}/email-resend/domains`, {
    headers: authHeaders(),
  });
  return data.data;
};

/**
 * Serie diaria de envíos para el gráfico del panel. Solo admin.
 *
 * `companyId` vacío = todas las empresas juntas.
 */
export const getEmailStats = async ({ days = 30, companyId = '' } = {}) => {
  const params = new URLSearchParams({ days: String(days) });
  if (companyId) params.set('company_id', companyId);

  const { data } = await axios.get(`${baseAPI}/email-resend/stats?${params}`, {
    headers: authHeaders(),
  });
  return data.data;
};

/**
 * Cancela un alta que todavía NO está verificada.
 *
 * El backend rechaza las verificadas con un 409: esas tienen hilos abiertos con
 * deudores y se desconectan con `scripts/disconnectEmailDomain.js`, no desde una
 * pantalla de perfil.
 */
export const disconnectEmailDomain = async domainId => {
  const { data } = await axios.delete(`${baseAPI}/email-resend/domains/${domainId}`, {
    headers: authHeaders(),
  });
  return data.data;
};

/** Levanta una pausa por salud. Solo admin. */
export const resumeEmailDomain = async domainId => {
  const { data } = await axios.post(
    `${baseAPI}/email-resend/domains/${domainId}/resume`,
    {},
    { headers: authHeaders() }
  );
  return data.data;
};

// --- Plantillas -------------------------------------------------------------
// Todas admin-only hoy. Cuando se abran al cliente, el backend cambia el guard
// de rol a `canManageCompanyEmail` y esto no se toca.

/** El catálogo estándar de ClaimUp. */
export const listTemplateCatalogue = async () => {
  const { data } = await axios.get(`${baseAPI}/email-resend/templates`, {
    headers: authHeaders(),
  });
  return data.data;
};

/** Edita el texto maestro del catálogo. No propaga a las copias ya repartidas. */
export const updateStandardTemplate = async (key, patch) => {
  const { data } = await axios.patch(`${baseAPI}/email-resend/templates/${key}`, patch, {
    headers: authHeaders(),
  });
  return data.data;
};

/** Las plantillas de un cliente, con su peldaño. La escalera va primero. */
export const listCompanyTemplates = async companyId => {
  const { data } = await axios.get(`${baseAPI}/email-resend/companies/${companyId}/templates`, {
    headers: authHeaders(),
  });
  return data.data;
};

/** Copia una del catálogo a la ficha del cliente. */
export const addTemplateFromCatalogue = async (companyId, key) => {
  const { data } = await axios.post(
    `${baseAPI}/email-resend/companies/${companyId}/templates/from-catalogue`,
    { key },
    { headers: authHeaders() }
  );
  return data.data;
};

/** Cambia el texto o el peldaño de la copia de un cliente. */
export const updateCompanyTemplate = async (companyId, key, patch) => {
  const { data } = await axios.patch(`${baseAPI}/email-resend/companies/${companyId}/templates/${key}`, patch, {
    headers: authHeaders(),
  });
  return data.data;
};

/** Quita una plantilla del cliente. El catálogo no se toca. */
export const removeCompanyTemplate = async (companyId, key) => {
  const { data } = await axios.delete(`${baseAPI}/email-resend/companies/${companyId}/templates/${key}`, {
    headers: authHeaders(),
  });
  return data.data;
};

/** Cuándo manda la secuencia automática de este cliente. */
export const getEmailCadence = async companyId => {
  const { data } = await axios.get(`${baseAPI}/email-resend/companies/${companyId}/cadence`, {
    headers: authHeaders(),
  });
  return data.data;
};

/** Guarda días, hora y demás. Upsert: la fila nace al tocar la pantalla. */
export const saveEmailCadence = async (companyId, patch) => {
  const { data } = await axios.put(`${baseAPI}/email-resend/companies/${companyId}/cadence`, patch, {
    headers: authHeaders(),
  });
  return data.data;
};

// --- Marca --------------------------------------------------------------

/** Logo, color, firma y pie de los correos de esta empresa. */
export const getEmailBrand = async companyId => {
  const { data } = await axios.get(`${baseAPI}/email-resend/companies/${companyId}/brand`, {
    headers: authHeaders(),
  });
  return data.data;
};

/** Guarda color, firma y pie. El logo va aparte, por multipart. */
export const saveEmailBrand = async (companyId, patch) => {
  const { data } = await axios.put(`${baseAPI}/email-resend/companies/${companyId}/brand`, patch, {
    headers: authHeaders(),
  });
  return data.data;
};

/**
 * Sube el logo.
 *
 * Sin `Content-Type` a mano: lo pone el navegador con el `boundary` del
 * FormData. Escribirlo nosotros deja el boundary fuera y el backend recibe un
 * multipart que no puede parsear.
 */
export const uploadEmailLogo = async (companyId, file) => {
  const form = new FormData();
  form.append('logo', file);
  const { data } = await axios.post(
    `${baseAPI}/email-resend/companies/${companyId}/brand/logo`,
    form,
    { headers: authHeaders() }
  );
  return data.data;
};

export const removeEmailLogo = async companyId => {
  const { data } = await axios.delete(
    `${baseAPI}/email-resend/companies/${companyId}/brand/logo`,
    { headers: authHeaders() }
  );
  return data.data;
};

/**
 * La vista previa del correo.
 *
 * La compone el BACKEND, con la misma funcion que manda los correos de verdad.
 * Es la unica forma de que la previa no mienta: una maqueta parecida escrita en
 * el front divergiria en la primera correccion que se le haga a una de las dos,
 * y entonces lo que se aprueba en pantalla no seria lo que recibe el deudor.
 */
export const previewEmailBrand = async (companyId, { body_text, company_name } = {}) => {
  const { data } = await axios.post(
    `${baseAPI}/email-resend/companies/${companyId}/brand/preview`,
    { body_text, company_name },
    { headers: authHeaders() }
  );
  return data.data;
};

/**
 * El numero de WhatsApp de esta empresa, para ofrecer ese boton.
 *
 * Lo resuelve el backend: la regla de que un numero cuenta solo si esta
 * conectado es del modulo de Kapso, y el formato que quiere `wa.me` —solo
 * digitos, sin «+» ni espacios— es facil de equivocar.
 */
export const getEmailWhatsappTarget = async companyId => {
  const { data } = await axios.get(
    `${baseAPI}/email-resend/companies/${companyId}/whatsapp-target`,
    { headers: authHeaders() }
  );
  return data.data;
};

/** Crea las plantillas de arranque que falten en el catálogo. Idempotente. */
export const seedTemplateCatalogue = async () => {
  const { data } = await axios.post(`${baseAPI}/email-resend/templates/seed`, {}, { headers: authHeaders() });
  return data.data;
};

/** Crea una plantilla nueva en el catálogo estándar. */
export const createStandardTemplate = async payload => {
  const { data } = await axios.post(`${baseAPI}/email-resend/templates`, payload, {
    headers: authHeaders(),
  });
  return data.data;
};

/** Crea una plantilla propia de un cliente, fuera del catálogo. */
export const createCompanyTemplate = async (companyId, payload) => {
  const { data } = await axios.post(`${baseAPI}/email-resend/companies/${companyId}/templates`, payload, {
    headers: authHeaders(),
  });
  return data.data;
};
